import type { TersaImageModel } from '../types';
import { providers } from '../../providers';
import { kie } from '../kie';
import { wavespeed } from '../wavespeed';

// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// rmb to usd 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;
const COST_FACTOR = 1.5;

export const volcengineModels: Record<string, TersaImageModel> = {
  'seedream-v5.0-pro-uncensored': {
    // 国际版默认模型（优先级高于 grok 的 default: true；仅 international 标签用户可见，
    // 其他用户在过滤后的模型表中取不到，自然回退到 grok）。
    default: 2,
    chef: providers.bytedance,
    providers: [
      {
        ...providers.bytedance,
        model: undefined,
        model_create: wavespeed.image('bytedance/seedream-v5.0-pro'),
        model_edit: wavespeed.image('bytedance/seedream-v5.0-pro/edit'),
        getCost: ({ providerOptions } = {}) => {
          const resolution = (providerOptions?.image_resolution || '').toUpperCase();
          if (resolution === '1K') return 0.045 * COST_FACTOR;
          if (resolution === '2K') return 0.09 * COST_FACTOR;
          return 0.09 * COST_FACTOR;
        },
      },
    ],
    configByMode: {
      t2i: {
        sizes: ['21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['1K', '2K'],
      },
      i2i: {
        sizes: ['match', '21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['1K', '2K'],
        inlineMedia: {
          allowInline: true,
          image: { allow: true, maxCount: 10, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
        },
      },
    },
    precheckByMode: {
      t2i: ['prompt'],
      i2i: ['prompt', 'image'],
    },
    imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    supportsInput: ['t2i', 'i2i'],
    supportsEdit: true,
    supportsImageType: 'url',
    priceIndicator: 'lowest',
    price: 'Dynamic',
  },
  'seedream-v5.0-pro': {
    chef: providers.bytedance,
    providers: [
      {
        ...providers.bytedance,
        model: undefined,
        model_create: kie.image('seedream-v5.0-pro'),
        modelBackup_create: [wavespeed.image('bytedance/seedream-v5.0-pro')],
        model_edit: kie.image('seedream-v5.0-pro'),
        modelBackup_edit: [wavespeed.image('bytedance/seedream-v5.0-pro/edit')],
        getCost: ({ providerOptions } = {}) => {
          const resolution = (providerOptions?.image_resolution || '').toUpperCase();
          if (resolution === '1K') return 0.045 * COST_FACTOR;
          if (resolution === '2K') return 0.09 * COST_FACTOR;
          return 0.09 * COST_FACTOR;
        },
      },
    ],
    configByMode: {
      t2i: {
        sizes: ['21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['1K', '2K'],
      },
      i2i: {
        sizes: ['match', '21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['1K', '2K'],
        inlineMedia: {
          allowInline: true,
          image: { allow: true, maxCount: 10, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
        },
      },
    },
    precheckByMode: {
      t2i: ['prompt'],
      i2i: ['prompt', 'image'],
    },
    imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    supportsInput: ['t2i', 'i2i'],
    supportsEdit: true,
    supportsImageType: 'url',
    priceIndicator: 'lowest',
    price: 'Dynamic',
  },
  'seedream-v5.0-flash': {
    chef: providers.bytedance,
    providers: [
      {
        ...providers.bytedance,
        model: undefined,
        model_create: kie.image('seedream-v5.0-flash'),
        modelBackup_create: [wavespeed.image('bytedance/seedream-v5.0-flash')],
        model_edit: kie.image('seedream-v5.0-flash'),
        modelBackup_edit: [wavespeed.image('bytedance/seedream-v5.0-flash/edit')],
        getCost: () => {
          return 0.027 * COST_FACTOR;
        },
      },
    ],
    configByMode: {
      t2i: {
        sizes: ['21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['1K', '2K'],
      },
      i2i: {
        sizes: ['match', '21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['1K', '2K'],
        inlineMedia: {
          allowInline: true,
          image: { allow: true, maxCount: 10, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
        },
      },
    },
    precheckByMode: {
      t2i: ['prompt'],
      i2i: ['prompt', 'image'],
    },
    imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    supportsInput: ['t2i', 'i2i'],
    supportsEdit: true,
    supportsImageType: 'url',
    priceIndicator: 'lowest',
    price: 'Dynamic',
  },
  'seedream-v5.0-lite': {
    chef: providers.bytedance,
    providers: [
      {
        ...providers.bytedance,
        model: undefined,
        model_create: kie.image('seedream-v5.0-lite'),
        modelBackup_create: [wavespeed.image('bytedance/seedream-v5.0-lite')],
        model_edit: kie.image('seedream-v5.0-lite'),
        modelBackup_edit: [wavespeed.image('bytedance/seedream-v5.0-lite/edit')],
        getCost: ({ providerOptions } = {}) => {
          const resolution = (providerOptions?.image_resolution || '').toUpperCase();
          if (resolution === '2K') return usd(0.22 * COST_FACTOR);
          if (resolution === '3K') return usd(0.33 * COST_FACTOR);
          if (resolution === '4K') return usd(0.44 * COST_FACTOR);
          return usd(0.44 * COST_FACTOR);
        },
      },
    ],
    configByMode: {
      t2i: {
        sizes: ['21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['2K', '3K', '4K'],
      },
      i2i: {
        sizes: ['match', '21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['2K', '3K', '4K'],
        inlineMedia: {
          allowInline: true,
          image: { allow: true, maxCount: 14, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
        },
      },
    },
    precheckByMode: {
      t2i: ['prompt'],
      i2i: ['prompt', 'image'],
    },
    imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    supportsInput: ['t2i', 'i2i'],
    supportsEdit: true,
    supportsImageType: 'url',
    priceIndicator: 'lowest',
    price: 'Dynamic',
  },
  'seedream-v4.5': {
    chef: providers.bytedance,
    providers: [
      {
        ...providers.bytedance,
        model: undefined,
        model_create: kie.image('seedream-v4.5'),
        modelBackup_create: [wavespeed.image('bytedance/seedream-v4.5')],
        model_edit: kie.image('seedream-v4.5'),
        modelBackup_edit: [wavespeed.image('bytedance/seedream-v4.5/edit')],
        getCost: ({ providerOptions } = {}) => {
          const resolution = (providerOptions?.image_resolution || '').toUpperCase();
          if (resolution === '2K') return usd(0.25 * COST_FACTOR);
          if (resolution === '4K') return usd(0.25 * COST_FACTOR);
          return usd(0.25 * COST_FACTOR);
        },
      },
    ],
    configByMode: {
      t2i: {
        sizes: ['21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['2K', '4K'],
      },
      i2i: {
        sizes: ['match', '21x9', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3', '1x1'],
        resolutions: ['2K', '4K'],
        inlineMedia: {
          allowInline: true,
          image: { allow: true, maxCount: 14, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
        },
      },
    },
    precheckByMode: {
      t2i: ['prompt'],
      i2i: ['prompt', 'image'],
    },
    imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    supportsInput: ['t2i', 'i2i'],
    supportsEdit: true,
    supportsImageType: 'url',
    priceIndicator: 'lowest',
    price: 'Dynamic',
  },
};